import { graphql } from 'react-relay';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import type { RestrictionsRESTObjForContentApi } from '@confluence/restrictions/entry-points/RestrictionsREST.ts';
import type { GqlRestrictionsInput } from '@confluence/restrictions/entry-points/convertRestrictionsToGqlRestrictionsInput.ts';
import type { RetrieveEditorTitleAndContent } from '@confluence/editor-features/entry-points/useRetrieveEditorTitleAndContent.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { handleEditorPublishPageAsBlogError } from './PublishPageAsBlogCleanup.tsx';
import { PublishPageAsBlogSources } from './PublishPageAsBlogSourcesEnum.tsx';
import type { PublishPageAsBlogMutationType, LinkType } from './types.ts';
import type {
	useConvertPageToBlogMutation$data,
	useConvertPageToBlogMutation$variables,
} from './__generated__/useConvertPageToBlogMutation.graphql.ts';

type OnConvertAndPublishSuccessCallback = (
	data: PublishPageAsBlogMutationType | useConvertPageToBlogMutation$data,
) => void;

type OnPublishFailCallback = ({
	links,
	errorMessage,
	flags,
}: {
	links: LinkType;
	errorMessage: string;
	flags: FlagsStateContainer;
}) => void;

type OnConvertFailCallback = ({
	error,
	flags,
}: {
	error: Error;
	flags: FlagsStateContainer;
}) => void;

type HandlePageToBlogConvert = ({
	flags,
	sourceAction,
	contentId,
	spaceKey,
	message,
	suppressNotifyWatchers,
	schedulePublishDate,
	retrieveEditorTitleAndContent,
	onConvertAndPublishSucceed,
	onPublishFail,
	onConvertFail,
	restrictions,
}: {
	flags: FlagsStateContainer;
	sourceAction: string;
	contentId: string;
	spaceKey: string | null | undefined;
	message?: string;
	suppressNotifyWatchers?: boolean;
	schedulePublishDate?: string;
	retrieveEditorTitleAndContent: RetrieveEditorTitleAndContent;
	onConvertAndPublishSucceed: OnConvertAndPublishSuccessCallback;
	onPublishFail: OnPublishFailCallback;
	onConvertFail: OnConvertFailCallback;
	restrictions?: RestrictionsRESTObjForContentApi | GqlRestrictionsInput;
}) => Promise<void>;

export const useConvertPageToBlog = () => {
	const { cloudId } = useSessionData();

	const [convertToBlogpostMutation] = useMutationInterop<
		useConvertPageToBlogMutation$data,
		useConvertPageToBlogMutation$variables
	>(graphql`
		mutation useConvertPageToBlogMutation(
			$contentId: ID!
			$content: String
			$message: String
			$suppressNotifyWatchers: Boolean
			$syncRev: String
			$ncsStepVersion: String
			$title: String
			$version: Int
			$schedulePublishDate: String
			$restrictions: PageRestrictionsInput
			$permissions: ConfluenceContentPermissionsInput
			$restrictionState: ConfluenceContentRestrictionStateInput
			$cloudId: ID!
			$destinationSpaceKey: String!
			$sourceStatus: String!
		) {
			confluence_convertToBlogpost(
				cloudId: $cloudId
				input: {
					confluenceContentInput: {
						contentBody: $content
						contentId: $contentId
						minorEdit: $suppressNotifyWatchers
						syncRev: $syncRev
						ncsStepVersion: $ncsStepVersion
						restrictions: $restrictions
						restrictionState: $restrictionState
						permissions: $permissions
						schedulePublishDate: $schedulePublishDate
						title: $title
						version: $version
						versionMessage: $message
					}
					destinationSpaceKey: $destinationSpaceKey
					sourceStatus: $sourceStatus
				}
			) {
				success
				content {
					status
					id
					links {
						webui
						editui
					}
				}
				errors {
					message
					extensions {
						statusCode
					}
				}
			}
		}
	`);

	const handlePageToBlogConversion: HandlePageToBlogConvert = async ({
		flags,
		sourceAction,
		contentId,
		spaceKey,
		message,
		suppressNotifyWatchers,
		schedulePublishDate,
		retrieveEditorTitleAndContent,
		onConvertAndPublishSucceed,
		onPublishFail,
		onConvertFail,
		restrictions,
	}) => {
		// Extract new permissions fields from GqlRestrictionsInput when using the
		// persistence_specific_access_gate experiment (new explicit permissions paradigm)
		const isGqlRestrictionsInput = (
			value: RestrictionsRESTObjForContentApi | GqlRestrictionsInput | undefined,
		): value is GqlRestrictionsInput =>
			value !== undefined && ('permissions' in value || 'restrictionState' in value);

		const gqlInput =
			isGqlRestrictionsInput(restrictions) &&
			expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')
				? restrictions
				: undefined;
		const permissionsInput = gqlInput?.permissions ?? undefined;
		const restrictionStateInput = gqlInput?.restrictionState ?? undefined;

		// When using new permissions paradigm, don't send legacy restrictions
		const legacyRestrictions = permissionsInput || restrictionStateInput ? undefined : restrictions;

		const variables: any = {
			contentId,
			destinationType: 'blogpost',
			sourceStatus: sourceAction === PublishPageAsBlogSources.EditPage ? 'draft' : 'current',
			destinationSpaceKey: spaceKey || '',
			cloudId,
			permissions: permissionsInput,
			restrictionState: restrictionStateInput,
		};

		try {
			if (sourceAction === PublishPageAsBlogSources.EditPage) {
				const { title, adf, syncRev, ncsStepVersion } = await retrieveEditorTitleAndContent();
				handleEditorPublishPageAsBlogError(adf);
				variables['content'] = JSON.stringify(adf);
				variables['title'] = title;
				variables['syncRev'] = syncRev;
				variables['ncsStepVersion'] = ncsStepVersion;
				variables['message'] = message;
				variables['suppressNotifyWatchers'] = suppressNotifyWatchers;
				variables['schedulePublishDate'] = schedulePublishDate;
				variables['restrictions'] = legacyRestrictions;
			}
			const { data } = await convertToBlogpostMutation({ variables });

			const hasConvertedAndPublished =
				(data as useConvertPageToBlogMutation$data)?.confluence_convertToBlogpost?.content
					?.status === 'current';
			const links = (data as useConvertPageToBlogMutation$data)?.confluence_convertToBlogpost
				?.content?.links;
			// it's possible for the conversion to go through but not the publishing of the blog draft
			if (data && hasConvertedAndPublished) {
				onConvertAndPublishSucceed(data);
			} else if (links?.editui && !schedulePublishDate) {
				onPublishFail({
					links,
					errorMessage: 'Page has converted to blog but not published',
					flags,
				});
			} else if (
				(data as useConvertPageToBlogMutation$data)?.confluence_convertToBlogpost?.errors?.[0]
			) {
				onConvertFail({
					error: new Error(
						(data as useConvertPageToBlogMutation$data)?.confluence_convertToBlogpost?.errors?.[0]
							?.message || 'Unknown error when converting page to blog',
					),
					flags,
				});
			}
		} catch (error) {
			onConvertFail({
				error,
				flags,
			});
		}
	};

	return handlePageToBlogConversion;
};
